import { useTranslations } from "next-intl";
import { useEffect } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { parseCurrencyAmount } from "@/app/lib/currency";

function calculateFee(spend: number): { base: number; percent: number; total: number } {
  if (spend >= 500000) {
    return { base: 26000, percent: 5, total: 26000 + Math.round(spend * 0.05) };
  } else if (spend >= 300000) {
    return { base: 21000, percent: 6, total: 21000 + Math.round(spend * 0.06) };
  } else if (spend >= 150000) {
    return { base: 15000, percent: 8, total: 15000 + Math.round(spend * 0.08) };
  } else {
    return { base: 12000, percent: 10, total: 12000 + Math.round(spend * 0.1) };
  }
}

function formatCzkNumber(amount: number): string {
  return amount.toString().replace(/\B(?=(\d{3})+(?!\d))/g, " ");
}

export default function EstimatedQuote() {
  const t = useTranslations("form");
  const { setValue, register, control } = useFormContext();

  const budgetValue = useWatch({ control, name: "monthlyBudget-controller", defaultValue: 50000 });
  const spend = parseCurrencyAmount(budgetValue) ?? 50000;
  const exceedsMax = spend > 500000;

  const fee = calculateFee(spend);

  const quoteText = exceedsMax
    ? t("feeScalable")
    : `${formatCzkNumber(fee.total)} Kč`;

  useEffect(() => {
    setValue("quote", quoteText);
  }, [quoteText, setValue]);

  return (
    <div className="flex flex-col items-center justify-center min-h-48 frame-neg px-4 py-8 mt-2 gap-2">
      <span className="mono opacity-60">{t("agencyFee")}</span>
      <span className="h3 uppercase text-center">
        {exceedsMax ? t("feeScalable") : (
          <>
            {formatCzkNumber(fee.total)} Kč
            <span className="opacity-60 text-base font-normal"> {t("perMonth")}</span>
          </>
        )}
      </span>
      {!exceedsMax && (
        <span className="mono opacity-60 text-center">
          {t("feeBreakdown", { base: formatCzkNumber(fee.base), percent: fee.percent })}
        </span>
      )}
      <input type="hidden" {...register("quote")} value={quoteText} />
    </div>
  );
}
